import { useState, useEffect } from 'react';

/**
 * Hook to track the current browser pathname, including changes triggered by:
 * - Browser back/forward buttons (popstate events)
 * - Programmatic navigation (pushState/replaceState)
 * - Remote app navigation
 *
 * @returns The current pathname string
 */
export function useCurrentPath(): string {
  const [currentPath, setCurrentPath] = useState<string>(window.location.pathname);

  useEffect(() => {
    let lastPath = window.location.pathname;

    const handleUrlChange = () => {
      const path = window.location.pathname;
      // Only react to real path changes — the initial call below and duplicate
      // notifications from pushState+popstate would otherwise emit twice.
      if (path === lastPath) return;
      lastPath = path;
      setCurrentPath(path);
    };

    // Listen to popstate events (browser back/forward buttons)
    window.addEventListener('popstate', handleUrlChange);

    // Intercept pushState and replaceState to catch programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      // Use setTimeout to ensure the URL has been updated
      setTimeout(handleUrlChange, 0);
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      // Use setTimeout to ensure the URL has been updated
      setTimeout(handleUrlChange, 0);
    };

    // Initialize with current pathname
    handleUrlChange();

    return () => {
      // Cleanup: remove event listener and restore original methods
      window.removeEventListener('popstate', handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  return currentPath;
}
